/**
 * Module system types
 */

/** Navigation item within a group */
export interface ModuleNavigationItem {
  label: string
  path: string
  icon: string
  badge?: string | number
  /** Only match exact URL — don't treat child paths as active */
  exact?: boolean
}

/** Navigation group containing items */
export interface ModuleNavigationGroup {
  label: string
  items: ModuleNavigationItem[]
}

/** Module navigation definition */
export interface ModuleNavigation {
  label: string
  icon: string
  groups: ModuleNavigationGroup[]
}

/** Fixture definition for seed data */
export interface ModuleFixture {
  file: string
  doctype: string
}

/** Schema extension definition */
export interface ModuleSchemaExtension {
  [tableName: string]: Record<string, string>
}

/** Branch scope configuration for multi-branch filtering */
export interface BranchScopeConfig {
  type: "none" | "single" | "multi-field" | "inherit"
  fields?: string[]       // branch field names to filter on
  createField?: string    // field to auto-fill branchId on create
}

/** Scope type for record visibility */
export type ScopeType = "all" | "own" | "own_and_assigned" | "assigned_only"

/** Lightweight lifecycle metadata for legacy module manifests. The richer
 * persisted contract lives on registeredDoctypes via @kubdee/sdk, but this
 * shape lets generic permission/workflow UI read action labels without
 * importing core from Tier 0. */
export interface ModuleDoctypeLifecycle {
  statusField?: string
  docStatusField?: string | null
  immutableStates?: string[]
  editAllowedStates?: string[]
  actions?: Array<{
    action: "submit" | "approve" | "reject" | "cancel" | "return" | "reopen" | "reverse" | "amend"
    label: string
    from: string[]
    to: string
    docStatus?: 0 | 1 | 2 | null
    reversalPolicy?: "none" | "status_only" | "reverse_gl" | "reverse_stock" | "credit_or_debit_note" | "amendment" | "custom"
  }>
}

/** Canonical lifecycle states exposed by platform/admin tooling */
export const MODULE_LIFECYCLE_STATES = [
  "available",
  "installed",
  "enabled",
  "disabled",
  "upgrading",
  "failed",
] as const

export type ModuleLifecycleState = (typeof MODULE_LIFECYCLE_STATES)[number]

/** Platform capabilities a module can require before tenant enablement */
export const PLATFORM_CAPABILITIES = [
  "platform_d1",
  "tenant_d1",
  "kv",
  "r2",
  "workers_ai",
  "dispatch_namespace",
  "internal_auth",
  "email",
  "browser_rendering",
] as const

export type PlatformCapability = (typeof PLATFORM_CAPABILITIES)[number]

/** Doctype definition for permission matrix + workflow builder */
export interface ModuleDoctype {
  key: string
  label: string
  type?: "normal" | "submittable"  // default: "normal". submittable = has docStatus lifecycle + workflows
  branchScope?: BranchScopeConfig  // default: { type: "none" } when not specified
  assignedField?: string  // field name that stores assigned user ID (e.g., "assignedTo", "salesPersonId")
  lifecycle?: ModuleDoctypeLifecycle
}

/** Hook declaration for workflow builder + documentation */
export interface ModuleHookDeclaration {
  /** Event name (e.g., "rental.request.createInvoice") */
  event: string
  /** Human-readable description (Thai) */
  label: string
}

/** Custom form declaration for workflow transitions */
export interface ModuleCustomForm {
  /** Unique key (e.g., "car-rental.deposit-payment") — matches workflow_transitions.customFormKey */
  key: string
  /** Human-readable description (Thai) */
  label: string
}

/** Full module configuration */
export interface ModuleConfig {
  name: string
  /** Display name (English) — synced to module_registry.name in DB */
  displayName?: string
  label: string
  icon: string
  /** Module color (hex) — synced to module_registry.color in DB */
  color?: string
  version: string
  dependencies: string[]

  schemas: Record<string, unknown>
  routes: Record<string, unknown>
  navigation: ModuleNavigation
  permissions: Record<string, unknown>
  hooks: Record<string, unknown>

  /** DocTypes this module provides (for permission matrix + workflow) */
  doctypes?: ModuleDoctype[]
  /** Action hooks this module can emit/handle (for workflow builder) */
  availableHooks?: ModuleHookDeclaration[]
  /** Custom form components registered for workflow transitions */
  customForms?: ModuleCustomForm[]

  extendSchemas?: ModuleSchemaExtension
  fixtures?: ModuleFixture[]

  // --- SDK / Marketplace fields (future) ---

  /** Seed version - tracks which seed version is bundled (defaults to version) */
  seedVersion?: string

  /** Module category for marketplace grouping */
  category?: 'core' | 'finance' | 'operations' | 'crm' | 'hr' | 'logistics' | 'custom'

  /** System module that cannot be disabled */
  isSystemModule?: boolean

  /** Seed function called on module enable */
  seed?: (db: unknown, companyId: string, userId: string) => Promise<void>

  /** Cleanup function called on module disable (future) */
  onDisable?: (db: unknown, companyId: string) => Promise<void>

  /** Minimum platform version required (future SDK) */
  minPlatformVersion?: string

  /**
   * Runtime capabilities that must exist before this module can be enabled
   * for a tenant. Dependency checks answer "which modules must be enabled";
   * capability checks answer "which platform bindings/features must exist".
   */
  requiredPlatformCapabilities?: PlatformCapability[]

  /** Module-specific SQL migrations — run when the module is enabled */
  moduleMigrations?: import('../module-system/migration-runner').ModuleMigrationDef[]

  /**
   * Doctypes this module registers into `core_doctype_registry` at boot.
   *
   * Doctype Admin Epic (#347) issue B1 (#356). Each entry must be produced
   * by `defineDoctype()` from `@kubdee/sdk` — that validator freezes the
   * object and narrows `features` to the `DoctypeFeature` union so we can't
   * accidentally persist a typo'd capability string.
   *
   * The platform boot loop in `apps/tenant-worker/src/middleware/auto-migrate.ts`
   * reads this field across every enabled module and calls `registerDoctypes()`
   * with the merged list. The boot glue is strictly generic — the type is
   * `readonly unknown[]` here on purpose so `packages/shared` does not have
   * to depend on `@kubdee/sdk` (which would break Tier 0). The boot code
   * itself re-asserts the shape via `validateDoctypeDefinition()` before
   * writing to the registry, so an accidental `any` slipping into this list
   * fails loud rather than landing a bogus row.
   */
  registeredDoctypes?: readonly unknown[]
}

/** Kubdee site configuration */
export interface KubdeeSiteConfig {
  name: string
  url: string
}

/** Cloudflare D1 binding config */
export interface D1BindingConfig {
  binding: string
}

/** Cloudflare R2 binding config */
export interface R2BindingConfig {
  binding: string
}

/** Cloudflare KV binding config */
export interface KVBindingConfig {
  binding: string
}

/** Root kubdee.config.ts type */
export interface KubdeeConfig {
  site: KubdeeSiteConfig
  modules: Record<string, boolean>
  d1: D1BindingConfig
  r2: R2BindingConfig
  kv: KVBindingConfig

  /**
   * Modules configured for independent dispatch (Phase 2).
   *
   * When a module is listed here with `true`, the tenant-worker dispatch
   * middleware will route requests to the module's dedicated Service Binding
   * via RPC instead of handling them in-process (bundled mode).
   *
   * Empty by default — all modules run in bundled mode until explicitly
   * opted-in here. RPC failures gracefully fall back to bundled mode.
   *
   * @example
   * independentModules: {
   *   'car-rental': true,
   *   'accounting': true,
   * }
   */
  independentModules?: Record<string, boolean>
}
